// Resumen (dashboard) logic of the Marketing module, pure.
import { endOfMonth, endOfWeek, startOfMonth, startOfWeek } from "date-fns";

import type { CompetitorMarketingActivity } from "@/components/competitors/types";

import { dayOf, parseDay, pieceDate, pieceDay } from "./calendar";
import { sumMetrics } from "./analytics";
import type { ContentItem, ContentMetric } from "./types";

export interface MarketingKpis {
  scheduledThisWeek: number;
  publishedThisMonth: number;
  leadsThisMonth: number;
  spendThisMonth: number;
}

const between = (day: string | null, start: string, end: string): boolean =>
  !!day && day >= start && day <= end;

/**
 * Pieces planned this week (any status), published this month, and leads /
 * spend of the weekly metric rows whose Monday falls in this month.
 */
export const marketingKpis = (
  items: readonly ContentItem[],
  metrics: readonly ContentMetric[],
  today: string,
): MarketingKpis => {
  const date = parseDay(today);
  const weekStart = dayOf(startOfWeek(date, { weekStartsOn: 1 }));
  const weekEnd = dayOf(endOfWeek(date, { weekStartsOn: 1 }));
  const monthStart = dayOf(startOfMonth(date));
  const monthEnd = dayOf(endOfMonth(date));
  const month = sumMetrics(
    metrics.filter((row) => between(row.week_start, monthStart, monthEnd)),
  );
  return {
    scheduledThisWeek: items.filter((item) =>
      between(pieceDay(item), weekStart, weekEnd),
    ).length,
    publishedThisMonth: items.filter(
      (item) =>
        item.status === "publicado" &&
        between(
          item.published_at
            ? dayOf(new Date(item.published_at))
            : pieceDay(item),
          monthStart,
          monthEnd,
        ),
    ).length,
    leadsThisMonth: month.leads,
    spendThisMonth: month.spend,
  };
};

/** Next pieces to publish (not published, planned from now on). */
export const upcomingPieces = (
  items: readonly ContentItem[],
  now: Date,
  limit = 6,
): ContentItem[] =>
  items
    .filter(
      (item) =>
        item.status !== "publicado" &&
        !!item.scheduled_at &&
        (pieceDate(item)?.getTime() ?? 0) >= now.getTime(),
    )
    .sort(
      (left, right) =>
        (pieceDate(left)?.getTime() ?? 0) - (pieceDate(right)?.getTime() ?? 0),
    )
    .slice(0, limit);

/** Latest competitor marketing activity (dated first, newest first). */
export const recentCompetitorActivity = (
  activity: readonly CompetitorMarketingActivity[],
  limit = 5,
): CompetitorMarketingActivity[] =>
  [...activity]
    .sort(
      (left, right) =>
        (right.published_at ?? "").localeCompare(left.published_at ?? "") ||
        (right.created_at ?? "").localeCompare(left.created_at ?? ""),
    )
    .slice(0, limit);
